import { Card, Table } from "react-bootstrap";
import type { DemographicBucket } from "@/types/analytics";
import EmptyState from "@/components/EmptyState";

type LegendItem = DemographicBucket & {
  color: string;
  percent: number;
};

const numberFormatter = new Intl.NumberFormat();
const percentFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatNumber = (value: number) =>
  numberFormatter.format(Math.round(value));
const formatPercent = (value: number) => `${percentFormatter.format(value)}%`;

// Brand sequence: teal → sage → gold, then the warm neutrals.
const genderPalette = ["#0F3D3E", "#8BA698", "#C49A48", "#c2d0c8"];
const agePalette = [
  "#0F3D3E",
  "#4f7b7c",
  "#8BA698",
  "#c2d0c8",
  "#C49A48",
  "#E5DED2",
  "#5d6b69",
];

const buildLegendItems = (
  items: DemographicBucket[],
  palette: string[]
): { total: number; items: LegendItem[] } => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  return {
    total,
    items: items.map((item, index) => ({
      ...item,
      color: palette[index % palette.length],
      percent: total > 0 ? (item.value / total) * 100 : 0,
    })),
  };
};

const DemographicTable = ({ items }: { items: LegendItem[] }) => (
  <Table responsive size="sm" className="mb-0 align-middle">
    <thead className="table-light">
      <tr>
        <th>Group</th>
        <th className="text-end">Users</th>
        <th className="text-end">Share</th>
      </tr>
    </thead>
    <tbody>
      {items.map((item) => (
        <tr key={item.id}>
          <td className="fw-semibold">
            <span
              className="me-2"
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                display: "inline-block",
                verticalAlign: "-1px",
                backgroundColor: item.color,
              }}
            />
            {item.label}
          </td>
          <td className="text-end">{formatNumber(item.value)}</td>
          <td className="text-end">
            <span className="pz-chip pz-chip--neutral">{formatPercent(item.percent)}</span>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const DemographicCard = ({
  title,
  items,
  palette,
  emptyTitle,
}: {
  title: string;
  items: DemographicBucket[];
  palette: string[];
  emptyTitle: string;
}) => {
  const { total, items: legendItems } = buildLegendItems(items, palette);

  return (
    <Card className="h-100">
      <Card.Header className="d-flex flex-wrap align-items-baseline justify-content-between gap-2">
        <span>{title}</span>
        <span className="text-muted small fw-normal">
          {formatNumber(total)} users
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        {legendItems.length === 0 ? (
          <EmptyState
            variant="compact"
            title={emptyTitle}
            body="Demographics fill in as people share them at sign-up."
          />
        ) : (
          <DemographicTable items={legendItems} />
        )}
      </Card.Body>
    </Card>
  );
};

export const GenderPieChartCard = ({
  items,
}: {
  items: DemographicBucket[];
}) => (
  <DemographicCard
    title="Gender breakdown"
    items={items}
    palette={genderPalette}
    emptyTitle="No gender data yet"
  />
);

export const AgeBarChartCard = ({ items }: { items: DemographicBucket[] }) => (
  <DemographicCard
    title="Age ranges"
    items={items}
    palette={agePalette}
    emptyTitle="No age data yet"
  />
);
